import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, formatApiError } from '../services/api';
import { UserIcon, EnvelopeIcon, LockClosedIcon, ExclamationCircleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { GoogleLogin } from '@react-oauth/google';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';

const Register = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Nama harus diisi';
    if (!formData.email) {
      newErrors.email = 'Email harus diisi';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Format email tidak valid';
    }
    if (!formData.password) {
      newErrors.password = 'Password harus diisi';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password minimal 6 karakter';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Password tidak cocok';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await authAPI.requestOtp({ email: formData.email });
      toast.success('Kode OTP telah dikirim ke email!');
      setStep(2);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return toast.error('Masukkan 6 digit OTP');

    setLoading(true);
    try {
      const response = await authAPI.register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        otp: otp
      });
      const { user: userData, token: authToken } = response.data;
      login(userData, authToken);
      toast.success('Registrasi berhasil!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    try {
      const response = await authAPI.googleAuth({ token: credentialResponse.credential });
      const { user: userData, token: authToken } = response.data;
      login(userData, authToken);
      toast.success('Pendaftaran dengan Google berhasil!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || 'Gagal mendaftar dengan Google');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center px-6 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="bg-white p-8 rounded-3xl shadow-soft">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Daftar Sekarang</h2>
          <p className="text-slate-600 mb-8">Buat akun untuk mulai patungin</p>

          {step === 1 ? (
            <form onSubmit={handleRequestOtp} className="space-y-5">
              <div>
                <label className="text-sm font-semibold text-slate-600 mb-2 block">Nama Lengkap</label>
                <div className="relative">
                  <UserIcon className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                  <input type="text" name="name" data-testid="register-name-input" value={formData.name} onChange={handleChange} placeholder="Masukkan nama lengkap"
                    className={`w-full pl-12 pr-4 py-3 rounded-[16px] border ${errors.name ? 'border-danger' : 'border-border'} focus:outline-none focus:ring-2 focus:ring-primary transition-all`} />
                </div>
                {errors.name && (<motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="text-xs text-danger mt-1 flex items-center gap-1"><ExclamationCircleIcon className="w-4 h-4" />{errors.name}</motion.p>)}
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-600 mb-2 block">Email</label>
                <div className="relative">
                  <EnvelopeIcon className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                  <input type="email" name="email" data-testid="register-email-input" value={formData.email} onChange={handleChange} placeholder="nama@email.com"
                    className={`w-full pl-12 pr-4 py-3 rounded-[16px] border ${errors.email ? 'border-danger' : 'border-border'} focus:outline-none focus:ring-2 focus:ring-primary transition-all`} />
                </div>
                {errors.email && (<motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="text-xs text-danger mt-1 flex items-center gap-1"><ExclamationCircleIcon className="w-4 h-4" />{errors.email}</motion.p>)}
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-600 mb-2 block">Password</label>
                <div className="relative">
                  <LockClosedIcon className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                  <input type={showPassword ? "text" : "password"} name="password" data-testid="register-password-input" value={formData.password} onChange={handleChange} placeholder="Minimal 6 karakter"
                    className={`w-full pl-12 pr-12 py-3 rounded-[16px] border ${errors.password ? 'border-danger' : 'border-border'} focus:outline-none focus:ring-2 focus:ring-primary transition-all`} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                    {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && (<motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="text-xs text-danger mt-1 flex items-center gap-1"><ExclamationCircleIcon className="w-4 h-4" />{errors.password}</motion.p>)}
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-600 mb-2 block">Konfirmasi Password</label>
                <div className="relative">
                  <LockClosedIcon className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                  <input type={showConfirmPassword ? "text" : "password"} name="confirmPassword" data-testid="register-confirm-password-input" value={formData.confirmPassword} onChange={handleChange} placeholder="Ulangi password"
                    className={`w-full pl-12 pr-12 py-3 rounded-[16px] border ${errors.confirmPassword ? 'border-danger' : 'border-border'} focus:outline-none focus:ring-2 focus:ring-primary transition-all`} />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                    {showConfirmPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
                {errors.confirmPassword && (<motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="text-xs text-danger mt-1 flex items-center gap-1"><ExclamationCircleIcon className="w-4 h-4" />{errors.confirmPassword}</motion.p>)}
              </div>

              <button type="submit" data-testid="register-submit-btn" disabled={loading}
                className="w-full bg-primary text-white py-3 rounded-full font-semibold hover:bg-primary-hover active:scale-95 transition-all shadow-float disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? 'Memproses...' : 'Lanjutkan'}
              </button>

              <div className="relative py-2 text-center">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <span className="relative bg-white px-4 text-xs text-slate-500 uppercase font-semibold tracking-wide">Atau</span>
              </div>

              <div className="flex justify-center w-full">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => toast.error('Gagal memuat Google Sign-In')}
                  useOneTap
                  theme="outline"
                  shape="pill"
                  width="100%"
                  text="signup_with"
                />
              </div>
            </form>
          ) : (
            <form onSubmit={handleVerifySubmit} className="space-y-6">
              <div className="text-center">
                <p className="text-sm text-slate-600 mb-4">Kode OTP telah dikirim ke <strong>{formData.email}</strong>. Berlaku selama 5 menit.</p>
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <button type="submit" disabled={loading || otp.length !== 6}
                  className="w-full bg-primary text-white py-3 rounded-full font-semibold hover:bg-primary-hover active:scale-95 transition-all shadow-float disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? 'Memverifikasi...' : 'Verifikasi OTP & Daftar'}
                </button>
                <button type="button" onClick={() => setStep(1)} disabled={loading}
                  className="w-full bg-transparent text-slate-500 py-3 rounded-full font-semibold hover:bg-slate-50 border border-slate-200 transition-all">
                  Kembali Edit Data
                </button>
              </div>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Sudah punya akun?{' '}
              <button onClick={() => navigate('/login')} data-testid="login-link" className="text-primary font-semibold hover:underline">Login di sini</button>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Register;
